import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../../../../config';

import ReportShell from '../../shared/ReportShell';
import ReportFilterBar from '../../shared/ReportFilterBar';
import ReportTable from '../../shared/ReportTable';
import ReportPagination from '../../shared/ReportPagination';
import ReportExportButtons from '../../shared/ReportExportButtons';
import ColumnPicker from '../../shared/ColumnPicker';
import useColumnConfig from '../../shared/useColumnConfig';
import useReportLookups from '../../shared/useReportLookups';
import useReportAccess from '../../shared/useReportAccess';

const PAGE_SIZE = 50;

const MONTH_OPTIONS = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

// Default to the previous calendar month (December if currently January).
const lastMonth = () => {
    const m = new Date().getMonth(); // 0–11
    return m === 0 ? '12' : String(m).padStart(2, '0');
};

const ALL_COLUMNS = [
    { key: 'tenant_name', label: 'Tenant Name' },
    { key: 'tenant_code', label: 'Tenant Code' },
    { key: 'outlet_code', label: 'Outlet Code' },
    { key: 'property_name', label: 'Property' },
    { key: 'audit_period', label: 'Audit Period' },
    { key: 'egoal_gross_value', label: 'EGoal Gross', isMoney: true },
    { key: 'egoal_tax_value', label: 'EGoal Tax', isMoney: true },
    { key: 'egoal_netsales_value', label: 'EGoal Net Sales', isMoney: true },
    { key: 'store_gross_value', label: 'Store Gross', isMoney: true },
    { key: 'store_tax_value', label: 'Store Tax', isMoney: true },
    { key: 'store_netsales_value', label: 'Store Net Sales', isMoney: true },
    { key: 'store_discount_value', label: 'Store Discount', isMoney: true },
    { key: 'store_return_value', label: 'Store Returns', isMoney: true },
    { key: 'difference_gross', label: 'Difference Gross', isMoney: true },
    { key: 'difference_tax', label: 'Difference Tax', isMoney: true },
    { key: 'difference_netsales', label: 'Difference Net Sales', isMoney: true },
    { key: 'audit_status', label: 'Status' },
    { key: 'audit_comments', label: 'Comments' },
    { key: 'discrepancy_remarks', label: 'Discrepancy Remarks' },
];

const DEFAULT_COLUMNS = ALL_COLUMNS.map((c) => c.key);

const initialFilters = {
    year: '',
    month: lastMonth(),
    property_id: '',
    selectedTenants: [],
};

const TenantAuditReportView = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const [years, setYears] = useState([]);
    const {
        selectedColumns, onChange: onColumnsChange, columnOptions,
        visibleColumns, exportColumns,
    } = useColumnConfig(ALL_COLUMNS, DEFAULT_COLUMNS);
    const [reportData, setReportData] = useState([]);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: '', dir: 'asc' });

    const lookups = useReportLookups({ propertyId: filters.property_id });
    const { concessionaireId, propertyAccessNames } = lookups;

    // Audit years are report-specific (not in the shared lookup loader).
    useEffect(() => {
        if (!concessionaireId) return;
        axios.post(`${config.hostname}/audit/getaudityears`, { concessionaire_id: concessionaireId }, { headers })
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                setYears(list);
                if (list.length) setFilters((prev) => (prev.year ? prev : { ...prev, year: String(list[0]) }));
            })
            .catch((err) => console.warn('audit years lookup failed', err?.response?.status));
    }, [concessionaireId, headers]);

    const yearOptions = useMemo(() => years.map((y) => ({ value: String(y), label: String(y) })), [years]);

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'property_id') next.selectedTenants = [];
            return next;
        });
        setReportData([]);
        setHasRun(false);
        setPage(0);
    };

    const resetFilters = () => {
        setFilters({ ...initialFilters, year: years.length ? String(years[0]) : '' });
        setReportData([]);
        setHasRun(false);
        setPage(0);
        setError('');
        setSort({ key: '', dir: 'asc' });
    };

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id: concessionaireId,
            module: 'Reports',
            task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const generateReport = async () => {
        setLoading(true);
        setError('');
        setPage(0);
        const payload = {
            concessionaire_id: concessionaireId,
            year: Number(filters.year),
            month: filters.month ? Number(filters.month) : null,
            property_access: propertyAccessNames,
            property_id: filters.property_id || null,
            tenant_id: filters.selectedTenants,
        };
        try {
            const res = await axios.post(`${config.hostname}/reports/gettenantauditreport`, payload, { headers });
            setReportData(Array.isArray(res.data) ? res.data : []);
            setHasRun(true);
            insertToLog(' generated Tenant Audit report');
        } catch (err) {
            console.error('tenant audit report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate Tenant Audit report'));
        } finally {
            setLoading(false);
        }
    };

    const sortedData = useMemo(() => {
        const data = [...reportData];
        if (!sort.key) return data;
        data.sort((a, b) => {
            const av = a[sort.key]; const bv = b[sort.key];
            if (av < bv) return sort.dir === 'asc' ? -1 : 1;
            if (av > bv) return sort.dir === 'asc' ? 1 : -1;
            return 0;
        });
        return data;
    }, [reportData, sort]);

    const pageCount = Math.ceil(sortedData.length / PAGE_SIZE);
    const pageRows = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const requestSort = (key) => {
        setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    };

    const fields = [
        { key: 'year', label: t('Year'), type: 'select', options: yearOptions, placeholder: t('Select year') },
        { key: 'month', label: t('Month'), type: 'select', options: MONTH_OPTIONS.map((o) => ({ ...o, label: t(o.label) })), placeholder: t('All months') },
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'selectedTenants', label: t('Tenants'), type: 'multiselect', options: lookups.tenantOptions, placeholder: t('All tenants'), span: 2 },
    ];

    const exportMetadata = useMemo(() => ([
        [t('Audit Period'), `${filters.month || t('All')}-${filters.year || ''}`],
        [t('Property'), nameOf(lookups.propertyOptions, filters.property_id) || t('All')],
        [t('Tenants'), filters.selectedTenants.length ? t('{{count}} selected', { count: filters.selectedTenants.length }) : t('All')],
        [t('Exported By'), login_name],
        [t('Exported On'), new Date().toLocaleString()],
    ]), [filters, lookups, login_name, t]);

    if (!allowed) return null;

    return (
        <Fragment>
            <ReportShell
                title={t('Tenant Audit Report')}
                subtitle={t('EGoal vs store-reported sales with audit status and discrepancy detail.')}
                actions={
                    <ReportExportButtons
                        heading={t('Tenant Audit Report')}
                        metadata={exportMetadata}
                        columns={exportColumns.map((c) => ({ ...c, header: t(c.header) }))}
                        data={sortedData}
                        fileName="TenantAuditReport"
                        landscape
                        onAfterExport={(fmt) => insertToLog(` exported Tenant Audit report to ${fmt}`)}
                    />
                }
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <ColumnPicker
                    options={columnOptions.map((o) => ({ ...o, label: t(o.label) }))}
                    value={selectedColumns}
                    total={ALL_COLUMNS.length}
                    onChange={onColumnsChange}
                />

                <ReportTable
                    columns={visibleColumns.map((c) => ({ ...c, label: t(c.label) }))}
                    rows={pageRows}
                    sort={sort}
                    onSort={requestSort}
                    loading={loading}
                    error={error}
                    emptyMessage={hasRun
                        ? t('No audit records found for the selected period and filters.')
                        : t('Choose your filters and select Generate report to view results.')}
                />

                <ReportPagination
                    page={page}
                    pageCount={pageCount}
                    onChange={setPage}
                    totalItems={sortedData.length}
                    pageSize={PAGE_SIZE}
                    itemLabel={t('records')}
                />
            </ReportShell>
        </Fragment>
    );
};

export default TenantAuditReportView;
